/**
 * Where an `add-to-chat` chip should land.
 *
 * A chip must go into the composer the user is already looking at. Starting a new
 * chat is only right when that composer belongs to a saved conversation the chip
 * should not pollute (panel closed, or the saved thread is out of scope). A blank
 * `new-*` chat IS the composer: replacing it (preferNew) drops the draft and any
 * chips already pasted there, and resets the dismissed "active document" badge.
 */
export type AddToChatTarget = {
  /** Call registry.createNewChat before inserting. */
  createNewChat: boolean
  /** Pass preferNew (always allocate) - only when replacing a saved conversation. */
  preferNew: boolean
}

export function resolveAddToChatTarget(opts: {
  collapsed: boolean
  hostedThreadId: string | null | undefined
  hostedOutOfScope: boolean
}): AddToChatTarget {
  const { collapsed, hostedThreadId, hostedOutOfScope } = opts

  if (!hostedThreadId) {
    // Nothing hosted: reuse an existing blank new chat if there is one.
    return { createNewChat: true, preferNew: false }
  }

  if (hostedThreadId.startsWith('new-')) {
    // Already a draft composer. Keep it unless the workspace filter moved away from it.
    return hostedOutOfScope
      ? { createNewChat: true, preferNew: false }
      : { createNewChat: false, preferNew: false }
  }

  // Saved conversation: closed panel or out-of-scope thread means "new chat, not the archive".
  return collapsed || hostedOutOfScope
    ? { createNewChat: true, preferNew: true }
    : { createNewChat: false, preferNew: false }
}

const FLASH_CLASSES = ['ring-2', 'ring-primary', 'ring-offset-1', 'transition-shadow']
const FLASH_MS = 1200

/** Make a skipped duplicate visible: scroll the existing chip into view and ring it briefly. */
export function flashReferenceChip(el: Element): void {
  if (!(el instanceof HTMLElement)) return
  el.scrollIntoView?.({ block: 'nearest', inline: 'nearest' })
  el.classList.add(...FLASH_CLASSES)
  window.setTimeout(() => el.classList.remove(...FLASH_CLASSES), FLASH_MS)
}
